package pl.decks.personal.components.widgets.pageprogress

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import pl.decks.personal.styles.SiteLayout

object PageProgressPointsBarStylesheet {
    object Vars {
        val lineWidth by StyleVariable<CSSLengthNumericValue>(SiteLayout.Line.Normal)
        val height by StyleVariable<CSSLengthOrPercentageNumericValue>(100.vh)
        val marginLeft by StyleVariable(SiteLayout.Space.Small)
        val paddingTopBottom by StyleVariable(SiteLayout.Space.XSmall)

        val color by StyleVariable<Color>()
        val activeColor by StyleVariable<Color>()
        val futureColor by StyleVariable<Color>()

        val pointSize by StyleVariable(20.px)
        val activePointSize by StyleVariable(30.px)
        val futurePointSize by StyleVariable(20.px)

        val lineStyle by StyleVariable(LineStyle.Solid)
        val activeLineStyle by StyleVariable(LineStyle.Solid)
        val futureLineStyle by StyleVariable(LineStyle.Dashed)

        val scrollTransitionDuration by StyleVariable(TransitionDurationVars.UltraSlow.value())
        val clickTransitionDuration by StyleVariable(TransitionDurationVars.Normal.value())

        val restingPaddingInternalSet by StyleVariable<CSSLengthOrPercentageNumericValue>(SiteLayout.Fraction.Half)
    }

    object Bar {
        val Container = CssStyle.base {
            Modifier
                .position(Position.Sticky)
                .top(SiteLayout.Fraction.none)
                .left(SiteLayout.Fraction.none)
                .minHeight(Vars.height.value())
                .padding(topBottom = Vars.paddingTopBottom.value())
                .margin(left = Vars.marginLeft.value())
        }
    }

    object Point {
        sealed interface Kind : ComponentKind

        val Style = CssStyle<Kind> {
            base {
                Modifier
                    .flexGrow(0)
                    .flexShrink(0)
                    .borderRadius(SiteLayout.Rounding.Full)
                    .padding(Vars.restingPaddingInternalSet.value())
                    .overflow(Overflow.Clip)
                    .transition {
                        property("size", "outline")
                        timingFunction(TransitionTimingFunction.EaseInOut)
                        duration(Vars.scrollTransitionDuration.value())
                    }
                    .transition {
                        property("padding")
                        timingFunction(TransitionTimingFunction.EaseInOut)
                        duration(Vars.clickTransitionDuration.value())
                    }
            }
            hover {
                Modifier.padding(SiteLayout.Fraction.Quarter)
            }
            active {
                Modifier.padding(SiteLayout.Fraction.none)
            }
        }
        val VisitedVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier
                .size(Vars.pointSize.value())
                .setVariable(Vars.restingPaddingInternalSet, SiteLayout.Space.none)
                .outline(
                    Vars.lineWidth.value(),
                    Vars.lineStyle.value(),
                    Vars.color.value(palette.color)
                )
        }
        val ActiveVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier
                .size(Vars.activePointSize.value())
                .setVariable(Vars.restingPaddingInternalSet, Vars.futurePointSize.value() / 3)
                .outline(
                    Vars.lineWidth.value(),
                    Vars.activeLineStyle.value(),
                    Vars.activeColor.value(Vars.color.value(palette.color)),
                )
        }
        val NextVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier
                .size(Vars.futurePointSize.value())
                .setVariable(Vars.restingPaddingInternalSet, Vars.futurePointSize.value() / 2)
                .outline(
                    Vars.lineWidth.value(),
                    Vars.futureLineStyle.value(),
                    Vars.activeColor.value(Vars.color.value(palette.color)),
                )
        }
        val FutureVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier
                .size(Vars.futurePointSize.value())
                .setVariable(Vars.restingPaddingInternalSet, Vars.futurePointSize.value() / 2)
                .outline(
                    Vars.lineWidth.value(),
                    Vars.futureLineStyle.value(),
                    Vars.futureColor.value(Vars.color.value(palette.color))
                )
        }
        val Content = CssStyle.base {
            val palette = colorMode.toPalette()
            Modifier
                .backgroundColor(Vars.activeColor.value(Vars.color.value(palette.color)))
                .borderRadius(SiteLayout.Rounding.Full)
                .fillMaxSize()
        }
    }

    object Line {
        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            Modifier.padding(SiteLayout.Space.none).margin(SiteLayout.Space.none).border(SiteLayout.Line.none)
        }
        val VisitedVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier.borderLeft(
                Vars.lineWidth.value(),
                Vars.lineStyle.value(),
                Vars.activeColor.value(Vars.color.value(palette.color)),
            )
        }
        val ActiveVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier.borderLeft(
                Vars.lineWidth.value(),
                Vars.futureLineStyle.value(),
                Vars.activeColor.value(Vars.color.value(palette.color)),
            )
        }
        val FutureVariant = Style.addVariantBase {
            val palette = colorMode.toPalette()
            Modifier.borderLeft(
                Vars.lineWidth.value(),
                Vars.futureLineStyle.value(),
                Vars.futureColor.value(Vars.color.value(palette.color)),
            )
        }
    }
}
